import ChatOutlined from '@mui/icons-material/ChatOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import GavelOutlined from '@mui/icons-material/GavelOutlined';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
import { ReportTranslationKeys } from 'intl/keys';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { DEFAULT_ISSUE_ATTACHMENTS_CONFIG_ON_VIEW } from 'shared/domain/report/mapping/reportConfigOnView';
import { ReportType } from 'shared/domain/report/types/model/common';
import {
  IssueAttachmentsConfigOnView,
  MessageSectionConfigOnView,
  SectionConfigOnView,
} from 'shared/domain/report/types/onView/common';
import {
  REPORT_CONTENT_LIST_SX,
  ReportAdditionalInformationSection,
} from '../../formUi';
import { Box } from '@mui/material';
import {
  useReportMessageAttachmentsAvailability,
  useReportSectionAvailability,
} from '../../contexts/ReportSectionAvailabilityContext';
import { useSyncUnavailableReportSections } from '../../hooks/useSyncUnavailableReportSections';
import { buildAttachmentsScopeLabel, updateDisplayStyle } from '../model';
import {
  IssueAttachmentsRowPresentational,
  MessageKindRowPresentational,
  SectionRowPresentational,
} from '../presentational';

export function IssueContentSelection({
  reportType,
}: {
  reportType: ReportType;
}): ReactElement {
  const intl = useIntl();
  const { values, setValues } = useInputForm();
  const availability = useReportSectionAvailability();
  const messageAttachmentsAvailability =
    useReportMessageAttachmentsAvailability();
  useSyncUnavailableReportSections(
    availability,
    messageAttachmentsAvailability,
    reportType
  );

  const config: IssueAttachmentsConfigOnView =
    values.issueAttachments ??
    DEFAULT_ISSUE_ATTACHMENTS_CONFIG_ON_VIEW(intl);

  const linkedDocumentation: SectionConfigOnView | undefined =
    values.linkedDocumentation;
  const events: MessageSectionConfigOnView | undefined = values.events;
  const incomingEmailMessages: MessageSectionConfigOnView | undefined =
    values.incomingEmailMessages;
  const workflowDecisions: MessageSectionConfigOnView | undefined =
    values.workflowDecisions;

  return (
    <Box sx={REPORT_CONTENT_LIST_SX} data-qa='report_content_list'>
      <IssueAttachmentsRowPresentational
        config={config}
        onConfigChange={(next) => setValues('issueAttachments', next)}
        sectionAvailability={availability.issueAttachments}
        onDisplayStyleChange={(displayStyle) =>
          setValues('issueAttachments', {
            ...config,
            displayStyle: updateDisplayStyle(intl, displayStyle),
          })
        }
        onScopeChange={(scope) =>
          setValues('issueAttachments', {
            ...config,
            scope: {
              _id: scope,
              label: buildAttachmentsScopeLabel(intl, scope),
            },
          })
        }
      />
      {linkedDocumentation && (
        <SectionRowPresentational
          icon={<FolderOutlined />}
          labelId={ReportTranslationKeys.sectionLinkedDocumentationLabel}
          config={linkedDocumentation}
          sectionAvailability={availability.linkedDocumentation}
          onConfigChange={(next) => setValues('linkedDocumentation', next)}
          data-qa='report_section_linked_documentation'
          onDisplayStyleChange={(displayStyle) =>
            setValues('linkedDocumentation', {
              ...linkedDocumentation,
              displayStyle: updateDisplayStyle(intl, displayStyle),
            })
          }
        />
      )}
      {events && (
        <MessageKindRowPresentational
          icon={<ChatOutlined />}
          labelId={ReportTranslationKeys.sectionEventsLabel}
          config={events}
          sectionAvailability={availability.events}
          attachmentsAvailability={messageAttachmentsAvailability.events}
          data-qa='report_section_events'
          onConfigChange={(next) => setValues('events', next)}
          onAttachmentsEnabledChange={(enabled) =>
            setValues('events', {
              ...events,
              attachments: { ...events.attachments, enabled },
            })
          }
          onDisplayStyleChange={(displayStyle) =>
            setValues('events', {
              ...events,
              attachments: {
                ...events.attachments,
                displayStyle: updateDisplayStyle(intl, displayStyle),
              },
            })
          }
        />
      )}
      {incomingEmailMessages && (
        <MessageKindRowPresentational
          icon={<EmailOutlined />}
          labelId={ReportTranslationKeys.sectionIncomingEmailMessagesLabel}
          config={incomingEmailMessages}
          sectionAvailability={availability.incomingEmailMessages}
          attachmentsAvailability={
            messageAttachmentsAvailability.incomingEmailMessages
          }
          data-qa='report_section_incoming_email_messages'
          onConfigChange={(next) =>
            setValues('incomingEmailMessages', next)
          }
          onAttachmentsEnabledChange={(enabled) =>
            setValues('incomingEmailMessages', {
              ...incomingEmailMessages,
              attachments: { ...incomingEmailMessages.attachments, enabled },
            })
          }
          onDisplayStyleChange={(displayStyle) =>
            setValues('incomingEmailMessages', {
              ...incomingEmailMessages,
              attachments: {
                ...incomingEmailMessages.attachments,
                displayStyle: updateDisplayStyle(intl, displayStyle),
              },
            })
          }
        />
      )}
      {workflowDecisions && (
        <MessageKindRowPresentational
          icon={<GavelOutlined />}
          labelId={ReportTranslationKeys.sectionWorkflowDecisionsLabel}
          config={workflowDecisions}
          sectionAvailability={availability.workflowDecisions}
          data-qa='report_section_workflow_decisions'
          onConfigChange={(next) => setValues('workflowDecisions', next)}
          showAttachments={false}
        />
      )}
      <ReportAdditionalInformationSection />
    </Box>
  );
}
